import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SlidersHorizontal,
  ListFilter,
  BarChart3,
  TrendingUp,
  HeartPulse,
  RotateCcw,
  Eye,
  GripHorizontal,
} from 'lucide-react';
import ConfigSectionShell, { SectionTab } from '../common/ConfigSectionShell';
import FilterBox, { DimensionState } from '../filters/FilterBox';
import {
  FILTER_BOX_CONFIGS,
  DimensionKey,
  FilterControlKey,
  FilterRow,
  isDimensionKey,
} from '../../config/filterBoxConfigs';

/**
 * FiltersConfigSection — editor WYSIWYG NO persistente de la caja de filtros (PT-#44, Fase 3).
 *
 * Item de menú "Filtros" del panel Administrar Cliente. Una tab por sección
 * (Publicaciones / Estadísticas / Tendencias / Data Health). Permite activar/desactivar
 * renglones y filtros de cada sección con PREVIEW en vivo de cómo queda el panel.
 *
 * DECISIÓN (PT-#44 GATE): esta versión es SOLO preview en memoria — lee los configs
 * hardcodeados de `filterBoxConfigs.ts` y togglea en `useState`. NO persiste en DB ni
 * llama al backend. La decisión global-vs-per-cliente + persistencia (Fases 1/2/4) queda
 * pendiente de OK de Gabriel; esto sirve para aprobar UI/UX con algo concreto enfrente.
 */

interface Props {
  clientId: string;
  clientName: string;
}

// Tabs en el orden del menú de secciones. Los ids mapean a las keys de FILTER_BOX_CONFIGS.
const SECTION_TABS: SectionTab[] = [
  { id: 'publicaciones', label: 'Publicaciones', icon: ListFilter, description: 'Caja de filtros de Publicaciones: 5 dimensiones + numéricos + búsqueda.' },
  { id: 'estadisticas', label: 'Estadísticas', icon: BarChart3, description: 'Filtros de Estadísticas: dimensiones + precio/descuento (sin posición ni pills).' },
  { id: 'tendencias', label: 'Tendencias', icon: TrendingUp, description: 'Filtros combinables de Tendencias: dimensiones + controles de análisis.' },
  { id: 'datahealth.hidden', label: 'Data Health', icon: HeartPulse, description: 'Filtros del listado de ocultos (Data Health): solo dimensionales.' },
];

// Label legible para cada control (dimensional + no dimensional).
const CONTROL_LABELS: Record<FilterControlKey, string> = {
  brand: 'Marca',
  site: 'Sitio',
  category: 'Categoría',
  subcategory: 'Subcategoría',
  segment: 'Segmento',
  price: 'Precio',
  position: 'Posición',
  discount: 'Descuento',
  globalSearch: 'Búsqueda global',
  metric: 'Métrica',
  priceType: 'Tipo de precio',
  period: 'Período',
  analysisLevel: 'Nivel de análisis',
  groupBy: 'Agrupar por',
};

// Options simuladas por dimensión para el preview (sin DB — WYSIWYG).
const MOCK_OPTIONS: Record<DimensionKey, string[]> = {
  brand: ['Samsung', 'LG', 'Sony', 'Philips', 'Noblex'],
  site: ['Frávega', 'Garbarino', 'Musimundo', 'Cetrogar'],
  category: ['TV', 'Audio', 'Electrodomésticos'],
  subcategory: ['Smart TV', 'Parlantes', 'Heladeras'],
  segment: ['Premium', 'Gama media', 'Entrada'],
};

// Construye un DimensionState de preview (options simuladas, setters no-op, counts fijos).
function mockDimension(key: DimensionKey): DimensionState {
  const options = MOCK_OPTIONS[key];
  const counts: Record<string, number> = {};
  options.forEach((o, i) => { counts[o] = (options.length - i) * 100; });
  return {
    filter: [],
    applied: [],
    pendingRemove: [],
    setFilter: () => {},
    options,
    meta: { counts, total: options.reduce((a, o) => a + counts[o], 0) },
    enabled: true,
  };
}

const PREVIEW_DIMENSIONS: Partial<Record<DimensionKey, DimensionState>> = {
  brand: mockDimension('brand'),
  site: mockDimension('site'),
  category: mockDimension('category'),
  subcategory: mockDimension('subcategory'),
  segment: mockDimension('segment'),
};

// Slots de preview para los controles NO dimensionales (inputs deshabilitados, solo para verse).
const previewSlot = (label: string, node: React.ReactNode) => (widthStyle?: React.CSSProperties) => (
  <div style={widthStyle} className="min-w-[180px]">
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    {node}
  </div>
);

const disabledInput = (placeholder: string) => (
  <input
    disabled
    placeholder={placeholder}
    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-gray-50 text-gray-400"
  />
);

const disabledSelect = (text: string) => (
  <div className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-gray-50 text-gray-400 flex items-center justify-between">
    <span>{text}</span>
    <span className="text-gray-300">▾</span>
  </div>
);

const PREVIEW_SLOTS: Partial<Record<FilterControlKey, (w?: React.CSSProperties) => React.ReactNode>> = {
  price: previewSlot('Precio', (
    <div className="flex gap-2">
      {disabledInput('$ Desde')}
      {disabledInput('$ Hasta')}
    </div>
  )),
  position: previewSlot('Posición', (
    <div className="flex gap-2">
      {disabledInput('Página')}
      {disabledInput('Orden')}
    </div>
  )),
  discount: previewSlot('Descuento', disabledSelect('Todos los productos')),
  globalSearch: previewSlot('Búsqueda global', disabledInput('Buscar términos…')),
  analysisLevel: previewSlot('Nivel de análisis', disabledSelect('Producto')),
  groupBy: previewSlot('Agrupar por', disabledSelect('Categoría')),
};

const FiltersConfigSection: React.FC<Props> = ({ clientId, clientName }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<string>('publicaciones');

  // Estado editable en memoria: rows por sección, sembrado del config hardcodeado.
  const seed = useMemo(() => {
    const map: Record<string, FilterRow[]> = {};
    Object.values(FILTER_BOX_CONFIGS).forEach(cfg => {
      map[cfg.sectionId] = cfg.rows.map(r => ({ fields: r.fields.map(f => ({ ...f })) }));
    });
    return map;
  }, []);
  const [editedRows, setEditedRows] = useState<Record<string, FilterRow[]>>(seed);

  const baseConfig = FILTER_BOX_CONFIGS[activeTab];
  const rows = editedRows[activeTab] || [];

  const toggleField = (rowIdx: number, fieldIdx: number) => {
    setEditedRows(prev => {
      const next = { ...prev };
      const sectionRows = next[activeTab].map((r, ri) =>
        ri !== rowIdx
          ? r
          : { fields: r.fields.map((f, fi) => (fi === fieldIdx ? { ...f, enabled: !f.enabled } : f)) },
      );
      next[activeTab] = sectionRows;
      return next;
    });
  };

  const toggleRow = (rowIdx: number) => {
    setEditedRows(prev => {
      const next = { ...prev };
      const row = next[activeTab][rowIdx];
      const allOn = row.fields.every(f => f.enabled);
      const sectionRows = next[activeTab].map((r, ri) =>
        ri !== rowIdx ? r : { fields: r.fields.map(f => ({ ...f, enabled: !allOn })) },
      );
      next[activeTab] = sectionRows;
      return next;
    });
  };

  const restoreSection = () => {
    setEditedRows(prev => ({
      ...prev,
      [activeTab]: (FILTER_BOX_CONFIGS[activeTab].rows || []).map(r => ({ fields: r.fields.map(f => ({ ...f })) })),
    }));
  };

  const isDirty = useMemo(
    () => JSON.stringify(editedRows[activeTab]) !== JSON.stringify(FILTER_BOX_CONFIGS[activeTab].rows),
    [editedRows, activeTab],
  );

  return (
    <ConfigSectionShell
      icon={SlidersHorizontal}
      title="Filtros"
      clientName={clientName}
      tabs={SECTION_TABS}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      {/* Banner no-persistente */}
      <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
        <Eye className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>
          <strong>Vista previa no persistente.</strong> Los cambios que hagas acá NO se guardan
          (PT-#44 Fase 3, sin backend). Sirven para ver cómo quedaría el panel de filtros de cada
          sección antes de decidir la persistencia (global vs per-cliente).
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Columna izquierda — editor de renglones/filtros */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
              <GripHorizontal className="w-4 h-4 text-gray-400" />
              Renglones y filtros
            </h4>
            <button
              type="button"
              onClick={restoreSection}
              disabled={!isDirty}
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-300 disabled:cursor-not-allowed"
            >
              <RotateCcw className="w-3 h-3" />
              Restaurar
            </button>
          </div>

          <div className="space-y-3">
            {rows.map((row, rowIdx) => {
              const allOn = row.fields.every(f => f.enabled);
              const noneOn = row.fields.every(f => !f.enabled);
              return (
                <div
                  key={rowIdx}
                  className={`rounded-lg border p-3 ${noneOn ? 'border-gray-200 bg-gray-50 opacity-70' : 'border-gray-200 bg-white'}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-500">Renglón {rowIdx + 1}</span>
                    <label className="flex items-center gap-1.5 cursor-pointer text-xs text-gray-600">
                      <input
                        type="checkbox"
                        checked={allOn}
                        ref={el => { if (el) el.indeterminate = !allOn && !noneOn; }}
                        onChange={() => toggleRow(rowIdx)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      Renglón completo
                    </label>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {row.fields.map((field, fieldIdx) => (
                      <label
                        key={field.key}
                        className={`flex items-center gap-1.5 px-2 py-1 rounded border cursor-pointer text-xs transition-colors ${
                          field.enabled
                            ? 'border-blue-200 bg-blue-50 text-blue-700'
                            : 'border-gray-200 bg-white text-gray-400'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={field.enabled}
                          onChange={() => toggleField(rowIdx, fieldIdx)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span>{CONTROL_LABELS[field.key] || field.key}</span>
                        {field.widthPct != null && (
                          <span className="text-[10px] text-gray-400 tabular-nums">{field.widthPct}%</span>
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Columna derecha — preview en vivo */}
        <div>
          <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-1.5 mb-2">
            <Eye className="w-4 h-4 text-gray-400" />
            Vista previa
          </h4>
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            {baseConfig ? (
              <FilterBox
                config={{ ...baseConfig, rows }}
                t={t}
                dimensions={PREVIEW_DIMENSIONS}
                slots={PREVIEW_SLOTS}
              />
            ) : (
              <p className="text-xs text-gray-400">Sección sin config.</p>
            )}
          </div>
          <p className="mt-2 text-[11px] text-gray-400">
            Preview con datos simulados. Los controles no dimensionales se muestran deshabilitados
            (solo layout). Cliente: <span className="tabular-nums">{clientId}</span>.
          </p>
        </div>
      </div>
    </ConfigSectionShell>
  );
};

export default FiltersConfigSection;
